import { computed, inject, Injectable, signal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {
  DpeState,
  PROPERTY_CREATION_STEP_SLUGS,
  PropertyCadastralDto,
  PropertyCharacteristicsDto,
  PropertyConstructionDto,
  PropertyCreationDto,
  PropertyCreationStep,
  PropertyDefinitionDto,
  PropertyDpeDto,
  PropertyDpeTechnicalDto,
  PropertyDto,
  PropertyFinancialsDto,
  PropertyId,
  PropertyKeyInfoDto,
  PropertyLocationDto,
  PropertyMandateDto,
  PropertyMediaDto,
  PropertyPresentationDto,
  PropertySellerDto,
  UserApplications,
} from '@workspace/data-types';
import { DtoChangedDetection, LocalStorage, StepperStep } from '@workspace/ng-aura';
import { ApplicationStore, LocalStorageKey } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { Observable, of, switchMap, tap } from 'rxjs';
import { DpeTechnicalUtils } from '../../../utils/dpe-technical.utils';
import { PropertyFormBuilder } from '../../../utils/property-form.builder';

@Injectable()
export class PropertyCreationStateService {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly localStorage = inject(LocalStorage);
  private readonly appStore = inject(ApplicationStore);

  readonly stepSlugs = PROPERTY_CREATION_STEP_SLUGS;

  readonly isOwnerApp = computed(() => this.appStore.currentApplicationName() === UserApplications.OWNER);

  readonly steps = signal<string[]>([
    'Définition du bien',
    'Localisation',
    'Cadastre',
    'Bilan énergétique',
    'Informations clés',
    'Construction',
    'Caractéristiques',
    'Géospatial',
    'Présentation',
    'Photos',
    'Finances',
  ]);

  readonly currentStep = signal<PropertyCreationStep>(PropertyCreationStep.DEFINITION);
  readonly property = signal<PropertyDto>(this.normalizeProperty(new PropertyDto()));
  readonly loading = signal<boolean>(false);
  readonly cadastralConfirmed = signal<boolean | null>(null);
  readonly currentStepFormValid = signal<boolean>(true);

  readonly computedSteps = computed<StepperStep[]>(() => {
    return this.steps().map((label, index) => ({
      label,
      completed: this.isStepCompleted(index as PropertyCreationStep),
    }));
  });

  readonly isCadastralConfirmed = computed(() => {
    const override = this.cadastralConfirmed();
    if (override !== null) return override;
    if (this.property()?._id && this.property()?.geospatial?.cadastral?.parcels?.length) {
      return true;
    }
    const reachedIndex = this.stepSlugs.indexOf(this.property()?.farthestStepReached);
    return reachedIndex !== -1 && reachedIndex > PropertyCreationStep.CADASTRAL;
  });

  setInitialProperty(prop: PropertyDto | null): void {
    this.property.set(this.normalizeProperty(prop || new PropertyDto()));
  }

  setCurrentStep(step: PropertyCreationStep): void {
    this.currentStep.set(step);
    this.currentStepFormValid.set(this.isStepCompleted(step));
  }

  setStepValidity(valid: boolean): void {
    this.currentStepFormValid.set(valid);
  }

  setCadastralConfirmed(confirmed: boolean): void {
    this.cadastralConfirmed.set(confirmed);
  }

  normalizeProperty(prop: PropertyDto): PropertyDto {
    if (!prop) return new PropertyDto();

    const defaultProp = new PropertyDto();
    const normalized = Object.assign(new PropertyDto(), prop);

    normalized.definition = prop.definition
      ? Object.assign(new PropertyDefinitionDto(), prop.definition)
      : defaultProp.definition;
    if (prop.definition?.seller) {
      normalized.definition.seller = Object.assign(new PropertySellerDto(), prop.definition.seller);
    }
    normalized.keyInfo = prop.keyInfo
      ? Object.assign(new PropertyKeyInfoDto(), prop.keyInfo)
      : defaultProp.keyInfo;
    normalized.characteristics = prop.characteristics
      ? Object.assign(new PropertyCharacteristicsDto(), prop.characteristics)
      : defaultProp.characteristics;
    normalized.construction = prop.construction
      ? Object.assign(new PropertyConstructionDto(), prop.construction)
      : defaultProp.construction;
    normalized.dpe = prop.dpe ? Object.assign(new PropertyDpeDto(), prop.dpe) : defaultProp.dpe;
    normalized.location = prop.location
      ? Object.assign(new PropertyLocationDto(), prop.location)
      : defaultProp.location;
    normalized.financials = prop.financials
      ? Object.assign(new PropertyFinancialsDto(), prop.financials)
      : defaultProp.financials;
    normalized.mandate = prop.mandate
      ? Object.assign(new PropertyMandateDto(), prop.mandate)
      : defaultProp.mandate || new PropertyMandateDto();
    normalized.presentation = prop.presentation
      ? Object.assign(new PropertyPresentationDto(), prop.presentation)
      : defaultProp.presentation;
    normalized.geospatial = prop.geospatial;

    if (prop.media) {
      normalized.media = Object.assign(new PropertyMediaDto(), prop.media);
      normalized.media.photos = prop.media.photos || [];
    } else {
      normalized.media = defaultProp.media;
      if (!normalized.media) {
        normalized.media = new PropertyMediaDto();
      }
      normalized.media.photos = [];
    }

    return normalized;
  }

  isOptionalStep(step: PropertyCreationStep): boolean {
    return step === PropertyCreationStep.CHARACTERISTICS || step === PropertyCreationStep.GEOHAZARDS;
  }

  isStepCompleted(step: PropertyCreationStep): boolean {
    if (this.appStore.debugMode()) {
      return true;
    }
    const prop = this.property();
    const reachedIndex = this.stepSlugs.indexOf(prop?.farthestStepReached);

    // If a later step was already reached, previous steps are completed (except DPE when DONE and invalid)
    if (reachedIndex !== -1 && reachedIndex > step) {
      if (step === PropertyCreationStep.DPE && prop.dpe?.state === DpeState.DONE) {
        return PropertyFormBuilder.isStepValid(step, prop, this.fb);
      }
      return true;
    }

    // Optional steps are only marked completed once reached
    if (this.isOptionalStep(step)) {
      return reachedIndex !== -1 && reachedIndex >= step;
    }

    return PropertyFormBuilder.isStepValid(step, prop, this.fb);
  }

  getFirstUncompletedStepIndex(): number {
    const stepsCount = this.steps().length;
    const prop = this.property();
    const reachedIndex = this.stepSlugs.indexOf(prop?.farthestStepReached);

    for (let i = 0; i < stepsCount; i++) {
      if (!this.isStepCompleted(i as PropertyCreationStep)) {
        return reachedIndex !== -1 ? Math.max(i, reachedIndex) : i;
      }
    }
    return stepsCount - 1;
  }

  onStepDtoChanged(step: PropertyCreationStep, event: DtoChangedDetection<unknown>): void {
    if (!event.dto) return;

    this.property.update((p) => {
      let newProperty = { ...p };
      switch (step) {
        case PropertyCreationStep.DEFINITION:
          newProperty.definition = event.dto as PropertyDefinitionDto;
          break;
        case PropertyCreationStep.LOCATION:
          newProperty.location = event.dto as PropertyLocationDto;
          break;
        case PropertyCreationStep.CADASTRAL:
          newProperty.geospatial = {
            ...newProperty.geospatial,
            cadastral: event.dto as PropertyCadastralDto,
          };
          break;
        case PropertyCreationStep.DPE: {
          const dpeDto = event.dto as PropertyDpeDto;
          newProperty.dpe = dpeDto;
          if (dpeDto?.technical) {
            newProperty = DpeTechnicalUtils.enrichPropertyFromTechnical(
              newProperty as PropertyDto,
              dpeDto.technical,
            );
          }
          break;
        }
        case PropertyCreationStep.KEY_INFOS:
          newProperty.keyInfo = {
            ...newProperty.keyInfo,
            ...(event.dto as Partial<PropertyKeyInfoDto>),
          };
          break;
        case PropertyCreationStep.CONSTRUCTION:
          newProperty.construction = {
            ...newProperty.construction,
            ...(event.dto as Partial<PropertyConstructionDto>),
          };
          break;
        case PropertyCreationStep.CHARACTERISTICS:
          newProperty.characteristics = event.dto as PropertyCharacteristicsDto;
          break;
        case PropertyCreationStep.FINANCIALS:
          newProperty.financials = event.dto as PropertyFinancialsDto;
          break;
        case PropertyCreationStep.PRESENTATION:
          newProperty.presentation = event.dto as PropertyPresentationDto;
          break;
        case PropertyCreationStep.PHOTOS:
          newProperty.media = event.dto as PropertyMediaDto;
          break;
      }
      return this.normalizeProperty(newProperty as PropertyDto);
    });
  }

  onCadastralUpdated(cadastral: PropertyCadastralDto): void {
    if (!cadastral) return;
    this.property.update((p) => {
      const newProperty = { ...p };
      newProperty.geospatial = {
        ...newProperty.geospatial,
        cadastral,
      };
      return this.normalizeProperty(newProperty as PropertyDto);
    });
    this.currentStepFormValid.set(Boolean(cadastral.parcels?.length));
  }

  onDpeTechnicalExtracted(technical: PropertyDpeTechnicalDto): void {
    if (!technical) return;
    this.property.update((p) => {
      const enriched = DpeTechnicalUtils.enrichPropertyFromTechnical(p, technical);
      return this.normalizeProperty(enriched);
    });
  }

  onGeospatialRefreshed(refreshedProperty: PropertyDto): void {
    if (refreshedProperty) {
      this.property.set(this.normalizeProperty(refreshedProperty));
    }
  }

  advanceFarthestStep(next: PropertyCreationStep): void {
    this.property.update((p) => {
      const newProp = Object.assign(new PropertyDto(), p);
      const reachedIndex = this.stepSlugs.indexOf(p.farthestStepReached);
      const farthestIndex = Math.max(reachedIndex, next);
      newProp.farthestStepReached = this.stepSlugs[farthestIndex];
      return this.normalizeProperty(newProp);
    });
  }

  saveProperty$(prop: PropertyDto): Observable<PropertyDto> {
    const payload: PropertyCreationDto = {
      definition: prop.definition,
      location: prop.location,
      geospatial: prop.geospatial?.cadastral
        ? {
            cadastral: prop.geospatial.cadastral,
          }
        : undefined,
      farthestStepReached: prop.farthestStepReached,
    };

    return this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .create(payload)
      .pipe(
        tap((savedProperty) => {
          this.property.set(this.normalizeProperty(savedProperty));
          this.localStorage.removeItem(LocalStorageKey.PROPERTY_DRAFT);
        }),
      );
  }

  patchProperty$(id: PropertyId, prop: PropertyDto, step: PropertyCreationStep): Observable<unknown> {
    const patch = this.api.business().etxe().properties().owner().patch(id);
    let obs$: Observable<unknown> = of(this.property());

    switch (step) {
      case PropertyCreationStep.DPE:
        obs$ = patch.dpe(prop.dpe).pipe(
          switchMap(() => this.api.business().etxe().properties().owner().get(id)),
          tap((fullProperty) => {
            if (fullProperty) {
              this.property.set(this.normalizeProperty(fullProperty));
            }
          }),
        );
        break;
      case PropertyCreationStep.KEY_INFOS:
        obs$ = patch.keyInfo(prop.keyInfo);
        break;
      case PropertyCreationStep.CONSTRUCTION:
        obs$ = patch.construction(prop.construction);
        break;
      case PropertyCreationStep.CHARACTERISTICS:
        obs$ = patch.characteristics(prop.characteristics);
        break;
      case PropertyCreationStep.GEOHAZARDS:
        obs$ = patch.geohazards().pipe(
          switchMap(() => this.api.business().etxe().properties().owner().get(id)),
          tap((fullProperty) => {
            if (fullProperty) {
              this.property.set(this.normalizeProperty(fullProperty));
            }
          }),
        );
        break;
      case PropertyCreationStep.PRESENTATION:
        obs$ = patch.presentation(prop.presentation);
        break;
      case PropertyCreationStep.PHOTOS:
        obs$ = patch.media(prop.media);
        break;
      case PropertyCreationStep.FINANCIALS:
        obs$ = patch.financials(prop.financials);
        break;
    }

    return obs$.pipe(
      tap((updatedResult: unknown) => {
        if (updatedResult && typeof updatedResult === 'object' && '_id' in updatedResult) {
          this.property.set(this.normalizeProperty(updatedResult as PropertyDto));
        }
      }),
    );
  }

  persistStep$(step: PropertyCreationStep): Observable<unknown> {
    const currentProp = this.property();
    if (step === PropertyCreationStep.CADASTRAL && !currentProp._id) {
      return this.saveProperty$(currentProp);
    } else if (currentProp._id) {
      return this.patchProperty$(currentProp._id, currentProp, step);
    }
    return of(currentProp);
  }

  clearDraft(): void {
    this.localStorage.removeItem(LocalStorageKey.PROPERTY_DRAFT);
  }
}
